// MARK://dhk8/pos/hooks/usePendingAcceptanceCount
// Conteo VIVO de órdenes por aceptar (status PENDING_ACCEPTANCE) para la "bolita"
// de la nav. El WS del canal `t:{tid}:orders` sólo DISPARA un refetch del conteo
// AUTORITATIVO por REST (robusto: no depende del shape del payload del evento).
// Fallback: poll lento cada 60s por si el WS está caído. Cuando el WS está vivo,
// la bolita reacciona casi al instante a order.created/accepted/rejected.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRealtimeSubscription } from '@dockethub/realtime'
import { listPendingAcceptanceOrders } from '../lib/clients/orders.js'
import { useAuthStore } from '../stores/auth-store.js'

const POLL_FALLBACK_MS = 60_000
const DEBOUNCE_MS = 400

export function usePendingAcceptanceCount(): number {
  const tenantId = useAuthStore((s) => s.activeTenantId())
  const [count, setCount] = useState(0)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const refetch = useCallback(async () => {
    if (tenantId === null) {
      setCount(0)
      return
    }
    try {
      const items = await listPendingAcceptanceOrders(tenantId)
      setCount(items.length)
    } catch {
      /* deja el último conteo conocido — no romper la nav por un fallo de red */
    }
  }, [tenantId])

  // Varios eventos seguidos (ráfaga) → un solo refetch.
  const bump = useCallback(() => {
    if (debounce.current !== null) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => { void refetch() }, DEBOUNCE_MS)
  }, [refetch])

  useEffect(() => {
    void refetch()
    const poll = setInterval(() => { void refetch() }, POLL_FALLBACK_MS)
    return () => {
      clearInterval(poll)
      if (debounce.current !== null) clearTimeout(debounce.current)
    }
  }, [refetch])

  // Canal vacío cuando no hay tenant → no-op (sin provider tampoco hay cliente).
  useRealtimeSubscription(tenantId !== null ? `t:${tenantId}:orders` : '', bump, [tenantId])

  return count
}
